import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icons";
import { fmtDate, relative } from "../lib/format";
import { openSheet, fitFor, prefetchSheets, type LiveSheet } from "../lib/pdfRender";
import { diffSheets } from "../lib/drawingDiff";
import type { DrawingNote, DrawingIssue, DrawingCloud } from "../lib/types";

/* The same drawing viewer as the Project Manager: a full-screen, full-quality
   sheet viewer with zoom/pan, sheet-to-sheet navigation, and measure / area /
   count takeoff. The original PDF is re-rasterised from the vector source at the
   current zoom (a real PDF viewer, not an upscaled JPEG) so it stays sharp all
   the way in. Prior revisions can be opened in place, and the smart-scan change
   regions are drawn as revision clouds. */

export interface ViewerSheet {
  id: string;
  code: string;
  title: string;
  discipline?: string;
  phase?: string;
  revision?: string;
  building?: string;
  issuedOn?: string;
  previewUrl?: string;
  fileUrl?: string;
  fileName?: string;
  page?: number;
  issues?: DrawingIssue[];
  clouds?: DrawingCloud[];
}

/** The stable key a note anchors to — building + number, so the same sheet
 *  number in two buildings keeps its notes apart. */
export function sheetKeyOf(s: ViewerSheet): string { return s.building ? `${s.building}/${s.code}` : s.code; }

const SHEET_W = 1100, SHEET_H = 780;
const ASPECT = SHEET_W / SHEET_H;

function isPdf(s: { fileUrl?: string; fileName?: string }) { return !!s.fileUrl && /\.pdf(\?|$)/i.test(s.fileName || s.fileUrl || ""); }
function isImage(s: { fileUrl?: string; fileName?: string }) { return !!s.fileUrl && /\.(png|jpe?g|webp)(\?|$)/i.test(s.fileName || s.fileUrl || ""); }

type HiState = "loading" | "full" | "preview";

/* ---- measuring: a scale is calibrated per sheet on this device ---------- */
interface Calibration { unitsPerFraction: number; unit: string }
const CAL_KEY = "atelier.sheetscale.v1";
function loadCal(id: string): Calibration | null {
  try { return (JSON.parse(localStorage.getItem(CAL_KEY) || "{}"))[id] ?? null; } catch { return null; }
}
function saveCal(id: string, c: Calibration | null) {
  try { const all = JSON.parse(localStorage.getItem(CAL_KEY) || "{}"); if (c) all[id] = c; else delete all[id]; localStorage.setItem(CAL_KEY, JSON.stringify(all)); } catch { /* quota */ }
}
interface Pt { x: number; y: number }
interface MLine { id: string; a: Pt; b: Pt }
interface Poly { id: string; pts: Pt[] }
interface Mark { id: string; x: number; y: number }
interface Rect { x: number; y: number; w: number; h: number }
type Tool = "pan" | "measure" | "area" | "count" | "note";

/** What the viewer needs to read + write revision notes on a sheet. Optional —
 *  omit to get a plain viewer with no markup. */
export interface NotesApi {
  sheetKey: (s: ViewerSheet) => string;
  notes: DrawingNote[];                 // all notes for the project; the viewer filters to the sheet
  by: string;
  author: "client" | "studio";
  onAdd: (n: { sheetKey: string; code: string; rev?: string; region?: Rect; body: string }) => void;
  onReply: (noteId: string, body: string) => void;
  onResolve?: (noteId: string, resolved: boolean) => void;   // studio only
}

let seq = 0;
const uid = (p: string) => `${p}${(seq++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function fLen(l: MLine) { const dx = (l.b.x - l.a.x) * ASPECT, dy = l.b.y - l.a.y; return Math.sqrt(dx * dx + dy * dy); }
function fArea(pts: Pt[]) { if (pts.length < 3) return 0; let a = 0; for (let i = 0; i < pts.length; i++) { const p = pts[i], q = pts[(i + 1) % pts.length]; a += (p.x * ASPECT) * q.y - (q.x * ASPECT) * p.y; } return Math.abs(a) / 2; }

/** A scalloped revision-cloud outline around a normalised box, in page space. */
function cloudPath(x: number, y: number, w: number, h: number, bump: number): string {
  const edge = (x1: number, y1: number, x2: number, y2: number) => {
    const len = Math.hypot(x2 - x1, y2 - y1);
    const n = Math.max(1, Math.round(len / (bump * 1.8)));
    const ux = (x2 - x1) / n, uy = (y2 - y1) / n;
    let d = "";
    for (let i = 0; i < n; i++) d += ` A ${bump} ${bump} 0 0 1 ${(x1 + ux * (i + 1)).toFixed(1)} ${(y1 + uy * (i + 1)).toFixed(1)}`;
    return d;
  };
  // clockwise winding — sweep-flag 1 bulges the arcs outward
  return `M ${x.toFixed(1)} ${y.toFixed(1)}`
    + edge(x, y, x + w, y) + edge(x + w, y, x + w, y + h)
    + edge(x + w, y + h, x, y + h) + edge(x, y + h, x, y) + " Z";
}

export function DrawingViewer({ sheet, registry, onNavigate, onClose, notesApi }: {
  sheet: ViewerSheet;
  registry?: ViewerSheet[];
  onNavigate?: (s: ViewerSheet) => void;
  onClose: () => void;
  notesApi?: NotesApi;
}) {
  const [tool, setTool] = useState<Tool>("pan");
  const [liveRect, setLiveRect] = useState<Rect | null>(null);
  const [composing, setComposing] = useState<{ region?: Rect } | null>(null);
  const [noteBody, setNoteBody] = useState("");
  const [panelOpen, setPanelOpen] = useState(true);
  const [reply, setReply] = useState<Record<string, string>>({});
  const [focusNote, setFocusNote] = useState<string | null>(null);
  const skey = notesApi ? notesApi.sheetKey(sheet) : "";
  const notes = notesApi ? notesApi.notes.filter((n) => n.sheetKey === skey) : [];
  const [scale, setScale] = useState(0.85);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [cal, setCal] = useState<Calibration | null>(() => loadCal(sheet.id));
  const [lines, setLines] = useState<MLine[]>([]);
  const [liveLine, setLiveLine] = useState<MLine | null>(null);
  const [calibrating, setCalibrating] = useState<{ line: MLine; value: string; unit: string } | null>(null);
  const [areas, setAreas] = useState<Poly[]>([]);
  const [areaPts, setAreaPts] = useState<Pt[]>([]);
  const [hoverPt, setHoverPt] = useState<Pt | null>(null);
  const [counts, setCounts] = useState<Mark[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isFs, setIsFs] = useState(false);
  const drag = useRef<{ kind: "pan" | "measure" | "note"; sx: number; sy: number; px: number; py: number; nx: number; ny: number } | null>(null);

  // revision compare + change clouds
  const issues = sheet.issues ?? [];
  const [viewRevIdx, setViewRevIdx] = useState(-1);        // -1 = current
  const [scanClouds, setScanClouds] = useState<DrawingCloud[] | null>(null);
  const [scanning, setScanning] = useState(false);
  const [showClouds, setShowClouds] = useState(true);
  const usingIssue = viewRevIdx >= 0 && issues[viewRevIdx];
  const active: ViewerSheet = usingIssue
    ? { ...sheet, revision: issues[viewRevIdx].rev, issuedOn: issues[viewRevIdx].issuedOn, previewUrl: issues[viewRevIdx].previewUrl, fileUrl: issues[viewRevIdx].fileUrl, fileName: issues[viewRevIdx].fileName ?? sheet.fileName, page: issues[viewRevIdx].page ?? sheet.page }
    : sheet;
  const isCurrentRev = viewRevIdx < 0 || viewRevIdx === issues.length - 1;
  const revClouds = usingIssue ? (issues[viewRevIdx].clouds ?? []) : (sheet.clouds ?? issues[issues.length - 1]?.clouds ?? []);
  const clouds = scanClouds ?? revClouds;
  const scanIdx = viewRevIdx < 0 ? issues.length - 1 : viewRevIdx;
  const canScan = scanIdx > 0 && !!issues[scanIdx - 1]?.previewUrl && !!(active.previewUrl || fitFor(active.fileUrl, active.page));

  // crisp live PDF canvas
  const [hiState, setHiState] = useState<HiState>("preview");
  const [canvasReady, setCanvasReady] = useState(false);
  const liveRef = useRef<LiveSheet | null>(null);
  const lastBackingRef = useRef(0);
  const scaleRef = useRef(scale);
  scaleRef.current = scale;

  function renderAtScale(force = false) {
    const ls = liveRef.current, cv = canvasRef.current;
    if (!ls || !cv) return;
    const dispW = SHEET_W * scaleRef.current;
    const target = Math.min(5200, Math.max(1500, Math.round(dispW * Math.min(window.devicePixelRatio || 1, 2))));
    const last = lastBackingRef.current;
    if (!force && last && target / last < 1.35 && target / last > 0.6) return;
    lastBackingRef.current = target;
    ls.render(cv, target).then(() => { setCanvasReady(true); setHiState("full"); }).catch(() => {});
  }

  // open (and keep parsed) the active source; re-render crisply as zoom changes
  useEffect(() => {
    liveRef.current?.destroy(); liveRef.current = null; lastBackingRef.current = 0;
    setCanvasReady(false);
    if (!active.fileUrl || !isPdf(active)) { setHiState(isImage(active) ? "full" : "preview"); return; }
    setHiState("loading");
    let alive = true;
    openSheet(active.fileUrl, active.page ?? 1).then((ls) => {
      if (!alive) { ls.destroy(); return; }
      liveRef.current = ls; renderAtScale(true);
    }).catch(() => { if (alive) setHiState("preview"); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.fileUrl, active.page]);

  useEffect(() => {
    if (!liveRef.current) return;
    const t = setTimeout(() => renderAtScale(false), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  useEffect(() => () => { liveRef.current?.destroy(); liveRef.current = null; }, []);

  function toggleFs() {
    const el = rootRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
  }
  useEffect(() => {
    const f = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", f);
    return () => document.removeEventListener("fullscreenchange", f);
  }, []);

  const canNav = !!registry && registry.length > 1 && !!onNavigate;
  const idx = canNav ? registry!.findIndex((d) => d.id === sheet.id) : -1;
  function jump(delta: number) { if (!canNav || idx < 0) return; const j = idx + delta; if (j >= 0 && j < registry!.length) onNavigate!(registry![j]); }

  // prefetch neighbours so next/prev opens instantly
  useEffect(() => {
    if (registry && idx >= 0) prefetchSheets(registry.slice(Math.max(0, idx - 1), idx + 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet.id]);

  // a fresh sheet resets zoom, pan, measurements and the revision view
  useEffect(() => {
    setScale(0.85); setPos({ x: 0, y: 0 }); setTool("pan");
    setLines([]); setLiveLine(null); setCalibrating(null); setCal(loadCal(sheet.id));
    setAreas([]); setAreaPts([]); setHoverPt(null); setCounts([]);
    setLiveRect(null); setComposing(null); setNoteBody(""); setFocusNote(null);
    setViewRevIdx(-1); setScanClouds(null); setShowClouds(true);
  }, [sheet.id]);

  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if (e.key === "Escape") { if (document.fullscreenElement) return; return onClose(); }
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowLeft") jump(-1);
      if (e.key === "ArrowRight") jump(1);
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(5, s * 1.15));
      if (e.key === "-") setScale((s) => Math.max(0.35, s * 0.87));
    };
    window.addEventListener("keydown", f);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", f); document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, registry, onNavigate, onClose]);

  async function scanVsPrev() {
    const prev = issues[scanIdx - 1];
    const curSrc = active.previewUrl || fitFor(active.fileUrl, active.page);
    if (!prev?.previewUrl || !curSrc) return;
    setScanning(true);
    try { const cl = await diffSheets(prev.previewUrl, curSrc); setScanClouds(cl); setShowClouds(true); }
    finally { setScanning(false); }
  }

  function norm(e: { clientX: number; clientY: number }): Pt {
    const r = wrapRef.current!.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  }
  /* Pointer events cover mouse, finger and stylus alike. Two fingers pinch-zoom
     (and never draw), so a tablet behaves the way an architect expects. */
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinch = useRef<{ dist: number; scale: number } | null>(null);
  function onDown(e: React.PointerEvent) {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {                    // second finger — start a pinch, cancel any draw
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, scale };
      drag.current = null; setLiveLine(null); setLiveRect(null);
      return;
    }
    if (pointers.current.size > 2) return;
    if (tool === "pan") { drag.current = { kind: "pan", sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y, nx: 0, ny: 0 }; }
    else if (tool === "measure") { const n = norm(e); drag.current = { kind: "measure", sx: e.clientX, sy: e.clientY, px: 0, py: 0, nx: 0, ny: 0 }; setLiveLine({ id: uid("ms-"), a: n, b: n }); }
    else if (tool === "area") {
      const n = norm(e);
      if (areaPts.length >= 3 && fLen({ id: "", a: areaPts[0], b: n }) < 0.02) return closeArea();
      setAreaPts((a) => [...a, n]);
    } else if (tool === "count") { const n = norm(e); setCounts((c) => [...c, { id: uid("ct-"), x: n.x, y: n.y }]); }
    else if (tool === "note") { const n = norm(e); drag.current = { kind: "note", sx: e.clientX, sy: e.clientY, px: 0, py: 0, nx: n.x, ny: n.y }; setLiveRect({ x: n.x, y: n.y, w: 0, h: 0 }); }
  }
  function onMove(e: React.PointerEvent) {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size >= 2) {     // pinch-to-zoom
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      setScale(Math.min(5, Math.max(0.35, pinch.current.scale * (dist / pinch.current.dist))));
      return;
    }
    if (tool === "area" && areaPts.length > 0 && !drag.current) setHoverPt(norm(e));
    const d = drag.current;
    if (!d) return;
    if (d.kind === "pan") setPos({ x: d.px + (e.clientX - d.sx), y: d.py + (e.clientY - d.sy) });
    else if (d.kind === "measure") { const n = norm(e); setLiveLine((l) => (l ? { ...l, b: n } : l)); }
    else if (d.kind === "note") { const n = norm(e); setLiveRect({ x: Math.min(d.nx, n.x), y: Math.min(d.ny, n.y), w: Math.abs(n.x - d.nx), h: Math.abs(n.y - d.ny) }); }
  }
  function onUp(e?: React.PointerEvent) {
    if (e?.pointerId != null) pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    const d = drag.current; drag.current = null;
    if (d?.kind === "measure" && liveLine) {
      if (fLen(liveLine) > 0.004) { if (!cal) setCalibrating({ line: liveLine, value: "", unit: "m" }); else setLines((x) => [...x, liveLine]); }
      setLiveLine(null);
    }
    if (d?.kind === "note") {
      const r = liveRect && liveRect.w > 0.01 && liveRect.h > 0.01 ? liveRect : undefined;
      setComposing({ region: r }); setNoteBody(""); setPanelOpen(true);
      if (!r) setLiveRect(null);
    }
  }
  function submitNote() {
    if (!notesApi || !composing || !noteBody.trim()) return;
    notesApi.onAdd({ sheetKey: skey, code: sheet.code, rev: sheet.revision, region: composing.region, body: noteBody.trim() });
    setComposing(null); setNoteBody(""); setLiveRect(null);
  }
  function onWheel(e: React.WheelEvent) { setScale((s) => Math.min(5, Math.max(0.35, s * (e.deltaY < 0 ? 1.12 : 0.89)))); }
  function commitCalibration() {
    if (!calibrating) return;
    const real = Number(calibrating.value); const frac = fLen(calibrating.line);
    if (!real || real <= 0 || frac <= 0) { setCalibrating(null); return; }
    const c: Calibration = { unitsPerFraction: real / frac, unit: calibrating.unit };
    saveCal(sheet.id, c); setCal(c); setLines((l) => [...l, calibrating.line]); setCalibrating(null);
  }
  function closeArea() { if (areaPts.length >= 3) setAreas((a) => [...a, { id: uid("ar-"), pts: areaPts }]); setAreaPts([]); setHoverPt(null); }
  const hasMeasured = lines.length || areas.length || areaPts.length || counts.length || cal;

  // keep the vector overlay constant in SCREEN space regardless of zoom
  const k = Math.min(2.6, Math.max(0.12, 1 / scale));
  const instant = fitFor(active.fileUrl, active.page) || active.previewUrl || (isImage(active) ? active.fileUrl : undefined);

  const overlay = (
    <div ref={rootRef} style={{ position: "fixed", inset: 0, zIndex: 95, display: "flex", flexDirection: "column", background: isFs ? "#131009" : "color-mix(in srgb, #131009 68%, transparent)", backdropFilter: "blur(8px)" }}>
      {/* header */}
      <div className="row between" style={{ padding: "12px 18px", background: "var(--card)", borderBottom: "1px solid var(--line)", gap: 12 }}>
        <div className="row" style={{ gap: 12, minWidth: 0 }}>
          <span className="badge accent mono" style={{ fontSize: 12 }}>{sheet.code}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sheet.title}</div>
            <div className="dim" style={{ fontSize: 11.5 }}>{[active.discipline, active.revision && `Rev ${active.revision}`, active.phase, active.issuedOn && `issued ${fmtDate(active.issuedOn)}`].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {/* revision selector */}
          {issues.length > 1 && (
            <select className="select" style={{ width: "auto", fontSize: 12 }} title="View a revision" value={viewRevIdx}
              onChange={(e) => { setViewRevIdx(Number(e.target.value)); setScanClouds(null); }}>
              <option value={-1}>Rev {sheet.revision || issues[issues.length - 1]?.rev} — current</option>
              {issues.slice().reverse().map((is) => { const i = issues.indexOf(is); if (i === issues.length - 1) return null;
                return <option key={i} value={i}>Rev {is.rev} — {fmtDate(is.issuedOn)}</option>; })}
            </select>
          )}
          {/* change clouds toggle / scan */}
          {clouds.length > 0
            ? <button className={"btn sm " + (showClouds ? "accent" : "ghost")} onClick={() => setShowClouds((v) => !v)} title="Show the regions that changed at this revision"><Icon.layers width={13} />Changes · {clouds.length}</button>
            : canScan && <button className="btn ghost sm" onClick={scanVsPrev} disabled={scanning} title="Smart-scan the differences from the previous revision"><Icon.target width={13} />{scanning ? "Scanning…" : "Scan changes"}</button>}
          {hiState === "loading" && <span className="badge" style={{ fontSize: 10.5 }}><span className="dot" />Rendering…</span>}
          {hiState === "full" && canvasReady && <span className="badge ok" style={{ fontSize: 10.5 }}><Icon.check width={11} />Full quality</span>}
          {hiState === "preview" && active.previewUrl && !active.fileUrl && <span className="badge" style={{ fontSize: 10.5 }} title="No original file on record — re-upload the sheet to attach it">Preview</span>}
          {active.fileUrl && (
            <>
              <a className="iconbtn" style={{ width: 32, height: 32, display: "inline-grid", placeItems: "center" }} title="Open the original in a new tab" target="_blank" rel="noreferrer" href={isPdf(active) ? `${active.fileUrl}#page=${active.page ?? 1}` : active.fileUrl}><Icon.arrowUpRight width={16} /></a>
              <a className="iconbtn" style={{ width: 32, height: 32, display: "inline-grid", placeItems: "center" }} title={`Download the original${active.fileName ? ` — ${active.fileName}` : ""}`} href={active.fileUrl} download={active.fileName || `${sheet.code}.pdf`}><Icon.download width={16} /></a>
            </>
          )}
          <button className="iconbtn" onClick={toggleFs} style={{ width: 32, height: 32 }} title={isFs ? "Exit full screen" : "Full screen"}><Icon.expand width={16} /></button>
          <button className="iconbtn" onClick={onClose} aria-label="Close"><Icon.close width={16} /></button>
        </div>
      </div>

      {/* viewing-a-past-revision banner */}
      {!isCurrentRev && (
        <div className="row between" style={{ padding: "7px 18px", background: "color-mix(in srgb, var(--accent) 12%, var(--card))", borderBottom: "1px solid var(--line)", fontSize: 12.5 }}>
          <span className="row" style={{ gap: 8 }}><Icon.back width={13} /><b>Viewing superseded revision {active.revision}</b> · {fmtDate(active.issuedOn)}{issues[viewRevIdx]?.note ? ` — ${issues[viewRevIdx].note}` : ""}</span>
          <button className="btn ghost sm" onClick={() => { setViewRevIdx(-1); setScanClouds(null); }}>Back to current</button>
        </div>
      )}

      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <div style={{ flex: 1, position: "relative", overflow: "hidden", touchAction: "none", cursor: tool === "pan" ? (drag.current ? "grabbing" : "grab") : "crosshair" }}
          onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={onUp} onWheel={onWheel}>

          {/* the sheet: instant bitmap under a crisp live canvas, vector overlay on top */}
          <div ref={wrapRef} onPointerDown={onDown} onDoubleClick={() => { if (tool === "area") closeArea(); }}
            style={{ position: "absolute", left: "50%", top: "50%", width: SHEET_W * scale, height: SHEET_H * scale, transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))`, background: "#fff", borderRadius: 4, boxShadow: "0 30px 80px -20px rgba(0,0,0,.55)" }}>
            {instant && <img src={instant} draggable={false} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", borderRadius: 4, opacity: canvasReady ? 0 : 1, transition: "opacity .2s" }} />}
            <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", borderRadius: 4, display: canvasReady ? "block" : "none" }} />
            {!instant && !canvasReady && (
              <div className="col center" style={{ position: "absolute", inset: 0, color: "#b8b2a6", fontSize: 15 }}>{sheet.code} — no drawing on file</div>
            )}

            {/* vector overlay — measurements, notes, clouds. Transparent + click-through
                except for the interactive note pins. */}
            <svg viewBox={`0 0 ${SHEET_W} ${SHEET_H}`} preserveAspectRatio="xMidYMid meet" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
              {/* revision clouds */}
              {showClouds && clouds.map((c, i) => {
                const x = c.x * SHEET_W, y = c.y * SHEET_H, w = c.w * SHEET_W, h = c.h * SHEET_H;
                const bump = Math.max(7, Math.min(w, h) * 0.14);
                return (
                  <g key={"cl" + i}>
                    <path d={cloudPath(x, y, w, h, bump)} fill="none" stroke="#d4341f" strokeWidth={2.6 * k} strokeLinejoin="round" strokeLinecap="round" />
                    <g transform={`translate(${x + w} ${y})`}>
                      <path d={`M 0 0 L ${9 * k} ${-15 * k} L ${18 * k} 0 Z`} fill="#d4341f" />
                      <text x={9 * k} y={-3 * k} textAnchor="middle" fill="#fff" style={{ fontSize: 9 * k, fontWeight: 700 }}>{(active.revision || "").replace(/[^\dA-Za-z]/g, "") || i + 1}</text>
                    </g>
                  </g>
                );
              })}

              {/* measurements */}
              {[...lines, ...(liveLine ? [liveLine] : [])].map((l) => {
                const x1 = l.a.x * SHEET_W, y1 = l.a.y * SHEET_H, x2 = l.b.x * SHEET_W, y2 = l.b.y * SHEET_H;
                const t = cal ? `${(fLen(l) * cal.unitsPerFraction).toFixed(2)} ${cal.unit}` : "set the scale";
                const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
                return (
                  <g key={l.id}>
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#7a3fa8" strokeWidth={2.4 * k} />
                    <circle cx={x1} cy={y1} r={4 * k} fill="#7a3fa8" /><circle cx={x2} cy={y2} r={4 * k} fill="#7a3fa8" />
                    <rect x={mx - (t.length * 4.6 + 8) * k} y={my - 26 * k} width={(t.length * 9.2 + 16) * k} height={24 * k} rx={12 * k} fill="#7a3fa8" />
                    <text x={mx} y={my - 9 * k} textAnchor="middle" fill="#fff" style={{ fontSize: 13 * k, fontWeight: 600 }}>{t}</text>
                  </g>
                );
              })}
              {/* finished areas */}
              {areas.map((poly, i) => {
                const cx = poly.pts.reduce((s, p) => s + p.x, 0) / poly.pts.length * SHEET_W;
                const cy = poly.pts.reduce((s, p) => s + p.y, 0) / poly.pts.length * SHEET_H;
                const label = cal ? `${(fArea(poly.pts) * cal.unitsPerFraction ** 2).toFixed(2)} ${cal.unit}²` : "set the scale";
                return (
                  <g key={poly.id}>
                    <polygon points={poly.pts.map((p) => `${p.x * SHEET_W},${p.y * SHEET_H}`).join(" ")} fill="#2f8f6b" fillOpacity={0.16} stroke="#2f8f6b" strokeWidth={2.2 * k} />
                    <rect x={cx - (label.length * 4.6 + 8) * k} y={cy - 13 * k} width={(label.length * 9.2 + 16) * k} height={24 * k} rx={12 * k} fill="#2f8f6b" />
                    <text x={cx} y={cy + 4 * k} textAnchor="middle" fill="#fff" style={{ fontSize: 13 * k, fontWeight: 600 }}>{label}</text>
                    <text x={cx} y={cy - 20 * k} textAnchor="middle" fill="#2f8f6b" style={{ fontSize: 11 * k, fontWeight: 700 }}>A{i + 1}</text>
                  </g>
                );
              })}
              {/* polygon being laid out */}
              {areaPts.length > 0 && (
                <g>
                  <polyline points={[...areaPts, ...(hoverPt ? [hoverPt] : [])].map((p) => `${p.x * SHEET_W},${p.y * SHEET_H}`).join(" ")} fill="none" stroke="#2f8f6b" strokeWidth={2.2 * k} strokeDasharray={`${7 * k} ${5 * k}`} />
                  {areaPts.map((p, i) => <circle key={i} cx={p.x * SHEET_W} cy={p.y * SHEET_H} r={(i === 0 ? 6 : 4.5) * k} fill="#2f8f6b" />)}
                </g>
              )}
              {/* count markers */}
              {counts.map((m, i) => (
                <g key={m.id} transform={`translate(${m.x * SHEET_W} ${m.y * SHEET_H})`}>
                  <circle r={12 * k} fill="#c6603d" /><text x={0} y={4 * k} textAnchor="middle" fill="#fff" style={{ fontSize: 12 * k, fontWeight: 700 }}>{i + 1}</text>
                </g>
              ))}
              {/* revision notes — highlighted regions with a numbered pin */}
              {notes.map((n, i) => {
                if (!n.region) return null;
                const col = n.resolved ? "#5c7a52" : n.author === "client" ? "#c6603d" : "#1c6fb8";
                const on = focusNote === n.id;
                return (
                  <g key={n.id} style={{ cursor: "pointer", pointerEvents: "auto" }} onClick={(e) => { e.stopPropagation(); setFocusNote(n.id); setPanelOpen(true); }}>
                    <rect x={n.region.x * SHEET_W} y={n.region.y * SHEET_H} width={n.region.w * SHEET_W} height={n.region.h * SHEET_H}
                      fill={col} fillOpacity={on ? 0.22 : 0.12} stroke={col} strokeWidth={(on ? 3 : 2) * k} strokeDasharray={n.resolved ? `${8 * k} ${5 * k}` : undefined} />
                    <g transform={`translate(${n.region.x * SHEET_W} ${n.region.y * SHEET_H})`}>
                      <circle r={11 * k} fill={col} /><text x={0} y={4 * k} textAnchor="middle" fill="#fff" style={{ fontSize: 12 * k, fontWeight: 700 }}>{i + 1}</text>
                    </g>
                  </g>
                );
              })}
              {/* the region being highlighted right now */}
              {liveRect && (liveRect.w > 0 || liveRect.h > 0) && (
                <rect x={liveRect.x * SHEET_W} y={liveRect.y * SHEET_H} width={liveRect.w * SHEET_W} height={liveRect.h * SHEET_H}
                  fill="#c6603d" fillOpacity={0.14} stroke="#c6603d" strokeWidth={2 * k} strokeDasharray={`${8 * k} ${5 * k}`} />
              )}
            </svg>
          </div>

          {/* tool rail */}
          <div className="col" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", gap: 6, background: "var(--card)", padding: 6, borderRadius: 14, border: "1px solid var(--line)", boxShadow: "var(--shadow-md)" }}>
            <button className={"iconbtn" + (tool === "pan" ? " on" : "")} style={railStyle(tool === "pan")} onClick={() => setTool("pan")} title="Pan (drag to move)"><Icon.move width={17} /></button>
            <button className={"iconbtn" + (tool === "measure" ? " on" : "")} style={railStyle(tool === "measure")} onClick={() => setTool("measure")} title={cal ? `Measure — 1 sheet width ≈ ${cal.unitsPerFraction.toFixed(2)} ${cal.unit}` : "Measure — draw along a known length first to set the scale"}><Icon.program width={16} /></button>
            <button className={"iconbtn" + (tool === "area" ? " on" : "")} style={railStyle(tool === "area")} onClick={() => setTool("area")} title="Area — click each corner, click the first again to close"><Icon.expand width={16} /></button>
            <button className={"iconbtn" + (tool === "count" ? " on" : "")} style={railStyle(tool === "count")} onClick={() => setTool("count")} title="Count / takeoff — click to drop a numbered marker"><Icon.target width={16} /></button>
            {notesApi && <>
              <div className="divider" style={{ margin: "2px 4px" }} />
              <button className={"iconbtn" + (tool === "note" ? " on" : "")} style={railStyle(tool === "note")} onClick={() => { setTool("note"); setPanelOpen(true); }} title="Note / revision — drag over an area to highlight it and add a comment"><Icon.chat width={16} /></button>
            </>}
            {hasMeasured ? <>
              <div className="divider" style={{ margin: "2px 4px" }} />
              <button className="iconbtn" style={railStyle(false)} title="Undo last" onClick={() => {
                if (tool === "area") { if (areaPts.length) setAreaPts((a) => a.slice(0, -1)); else setAreas((a) => a.slice(0, -1)); }
                else if (tool === "count") setCounts((c) => c.slice(0, -1));
                else setLines((l) => l.slice(0, -1));
              }}><Icon.back width={16} /></button>
              <button className="iconbtn" style={railStyle(false)} title="Clear everything measured & the scale" onClick={() => { setLines([]); setAreas([]); setAreaPts([]); setHoverPt(null); setCounts([]); saveCal(sheet.id, null); setCal(null); }}><Icon.close width={16} /></button>
            </> : null}
          </div>

          {/* measure status */}
          {(tool === "measure" || tool === "area") && (
            <div className="card" style={{ position: "absolute", right: 14, top: 14, zIndex: 12, padding: "10px 14px", maxWidth: 250, boxShadow: "var(--shadow-md)" }}>
              <div className="kicker" style={{ fontSize: 9, marginBottom: 6 }}>{tool === "area" ? "Area takeoff" : "Measuring"}</div>
              {!cal
                ? <div style={{ fontSize: 12.5, lineHeight: 1.5 }}><b>Set the scale first.</b> Drag along something of known length — a grid bay, a door, the drawn scale bar — then type what it really measures.</div>
                : tool === "area"
                  ? <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>Click each corner; click the first point again (or double-click) to close the shape.</div>
                  : <div style={{ fontSize: 12.5 }}>Scale set — drag along anything to measure it.</div>}
              {tool === "measure" && cal && lines.length > 0 && (
                <div className="col" style={{ gap: 3, marginTop: 8 }}>
                  {lines.slice(-6).map((l, i) => <div key={l.id} className="row between" style={{ fontSize: 12 }}><span className="dim">#{lines.length - Math.min(6, lines.length) + i + 1}</span><span className="mono" style={{ fontWeight: 600 }}>{(fLen(l) * cal.unitsPerFraction).toFixed(2)} {cal.unit}</span></div>)}
                  <div className="row between" style={{ fontSize: 12, borderTop: "1px solid var(--line)", marginTop: 4, paddingTop: 4 }}><span className="dim">Total</span><span className="mono" style={{ fontWeight: 700 }}>{lines.reduce((s, l) => s + fLen(l) * cal.unitsPerFraction, 0).toFixed(2)} {cal.unit}</span></div>
                </div>
              )}
              {tool === "area" && cal && areas.length > 0 && (
                <div className="col" style={{ gap: 3, marginTop: 8 }}>
                  {areas.slice(-6).map((poly, i) => <div key={poly.id} className="row between" style={{ fontSize: 12 }}><span className="dim">A{areas.length - Math.min(6, areas.length) + i + 1}</span><span className="mono" style={{ fontWeight: 600 }}>{(fArea(poly.pts) * cal.unitsPerFraction ** 2).toFixed(2)} {cal.unit}²</span></div>)}
                </div>
              )}
              {cal && <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={() => { saveCal(sheet.id, null); setCal(null); setLines([]); setAreas([]); }}><Icon.swap width={12} />Re-set the scale</button>}
            </div>
          )}
          {tool === "count" && counts.length > 0 && (
            <div className="card" style={{ position: "absolute", right: 14, top: 14, zIndex: 12, padding: "10px 14px", boxShadow: "var(--shadow-md)" }}>
              <div className="kicker" style={{ fontSize: 9 }}>Count</div>
              <div className="serif" style={{ fontSize: 26, marginTop: 2 }}>{counts.length}</div>
            </div>
          )}

          {/* zoom controls */}
          <div className="row" style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", gap: 4, background: "var(--card)", padding: 5, borderRadius: 12, border: "1px solid var(--line)", boxShadow: "var(--shadow-md)" }}>
            <button className="iconbtn" style={railStyle(false)} title="Zoom out" onClick={() => setScale((s) => Math.max(0.35, s * 0.85))}><Icon.minus width={16} /></button>
            <button className="btn ghost sm" style={{ minWidth: 58 }} title="Fit to screen" onClick={() => { setScale(0.85); setPos({ x: 0, y: 0 }); }}>{Math.round(scale * 100)}%</button>
            <button className="iconbtn" style={railStyle(false)} title="Zoom in" onClick={() => setScale((s) => Math.min(5, s * 1.15))}><Icon.plus width={16} /></button>
          </div>

          {/* prev / next */}
          {canNav && (
            <>
              <button className="iconbtn" style={{ ...navBtn, left: 14 }} title="Previous sheet (←)" disabled={idx <= 0} onClick={() => jump(-1)}><Icon.back width={20} /></button>
              <button className="iconbtn" style={{ ...navBtn, right: 14, transform: "translateY(-50%) rotate(180deg)" }} title="Next sheet (→)" disabled={idx >= registry!.length - 1} onClick={() => jump(1)}><Icon.back width={20} /></button>
              <div className="badge" style={{ position: "absolute", bottom: 18, right: 16, fontSize: 11 }}>{idx + 1} / {registry!.length}</div>
            </>
          )}
          {/* reopen tab when the notes panel is closed */}
          {notesApi && !panelOpen && (
            <button className="btn soft sm" style={{ position: "absolute", top: 14, right: 14, zIndex: 12 }} onClick={() => setPanelOpen(true)}>
              <Icon.chat width={14} />Notes{notes.length ? ` · ${notes.length}` : ""}
            </button>
          )}
        </div>

        {/* notes / revision panel */}
        {notesApi && panelOpen && (
          <div className="col" style={{ width: 330, flexShrink: 0, borderLeft: "1px solid var(--line)", background: "var(--card)", minHeight: 0 }}>
            <div className="row between" style={{ padding: "12px 16px", borderBottom: "1px solid var(--line)" }}>
              <span className="row" style={{ gap: 8 }}><Icon.chat width={15} /><b style={{ fontSize: 14 }}>Notes</b><span className="dim" style={{ fontSize: 12 }}>{notes.length || ""}</span></span>
              <button className="iconbtn" style={{ width: 28, height: 28 }} onClick={() => setPanelOpen(false)} title="Hide panel"><Icon.close width={14} /></button>
            </div>
            <div className="col" style={{ gap: 12, padding: 14, overflowY: "auto", flex: 1 }}>
              {/* composer */}
              {composing ? (
                <div className="panel" style={{ padding: 12, borderColor: "var(--accent)" }}>
                  <div className="kicker" style={{ fontSize: 9, marginBottom: 6 }}>{composing.region ? "Note on the highlighted area" : `Note on ${sheet.code}`}</div>
                  <textarea className="input" rows={3} autoFocus value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="What needs to change here?" />
                  <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                    <button className="btn ghost sm" onClick={() => { setComposing(null); setNoteBody(""); setLiveRect(null); }}>Cancel</button>
                    <button className="btn accent sm" disabled={!noteBody.trim()} onClick={submitNote}><Icon.send width={13} />Add note</button>
                  </div>
                </div>
              ) : (
                <button className="btn soft block" onClick={() => { setTool("note"); }} style={{ borderStyle: tool === "note" ? "solid" : "dashed" }}>
                  <Icon.chat width={14} />{tool === "note" ? "Drag over the drawing to highlight…" : "Add a note — highlight an area"}
                </button>
              )}

              {notes.length === 0 && !composing && <div className="dim" style={{ fontSize: 12.5, textAlign: "center", padding: "8px 0" }}>No notes on this sheet yet.</div>}

              {notes.map((n, i) => {
                const col = n.resolved ? "var(--ok)" : n.author === "client" ? "var(--accent)" : "var(--sky)";
                return (
                  <div key={n.id} className="panel" style={{ padding: "10px 12px", borderColor: focusNote === n.id ? col : "var(--line)", cursor: n.region ? "pointer" : "default" }} onClick={() => n.region && setFocusNote(n.id)}>
                    <div className="row between" style={{ gap: 8 }}>
                      <span className="row" style={{ gap: 7, minWidth: 0 }}>
                        <span style={{ width: 18, height: 18, borderRadius: 18, background: col, color: "#fff", display: "grid", placeItems: "center", fontSize: 10.5, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{n.by}</span>
                        <span className="badge" style={{ fontSize: 8.5 }}>{n.author}</span>
                      </span>
                      {n.resolved && <span className="badge ok" style={{ fontSize: 8.5 }}><Icon.check width={9} />Done</span>}
                    </div>
                    <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>{n.body}</div>
                    <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{n.rev ? `rev ${n.rev} · ` : ""}{relative(n.on)}{n.region ? " · highlighted" : ""}</div>
                    {(n.replies ?? []).map((r) => (
                      <div key={r.id} style={{ marginTop: 8, paddingLeft: 10, borderLeft: "2px solid var(--line-2)" }}>
                        <div className="row" style={{ gap: 6 }}><span style={{ fontSize: 11.5, fontWeight: 600 }}>{r.by}</span><span className="dim" style={{ fontSize: 10 }}>{relative(r.on)}</span></div>
                        <div style={{ fontSize: 12.5, marginTop: 2 }}>{r.body}</div>
                      </div>
                    ))}
                    <div className="row" style={{ gap: 6, marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                      <input className="input" style={{ fontSize: 12.5, padding: "5px 9px" }} placeholder="Reply…" value={reply[n.id] ?? ""} onChange={(e) => setReply((x) => ({ ...x, [n.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter" && (reply[n.id] ?? "").trim()) { notesApi.onReply(n.id, reply[n.id].trim()); setReply((x) => ({ ...x, [n.id]: "" })); } }} />
                      {notesApi.onResolve && <button className="iconbtn" style={{ width: 28, height: 28 }} title={n.resolved ? "Reopen" : "Mark resolved"} onClick={() => notesApi.onResolve!(n.id, !n.resolved)}><Icon.check width={14} style={{ color: n.resolved ? "var(--ink-3)" : "var(--ok)" }} /></button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* calibration prompt */}
      {calibrating && (
        <div style={{ position: "fixed", inset: 0, zIndex: 96, display: "grid", placeItems: "center", background: "rgba(0,0,0,.4)" }} onClick={() => setCalibrating(null)}>
          <div className="card pad" style={{ width: 340, maxWidth: "90vw" }} onClick={(e) => e.stopPropagation()}>
            <div className="kicker" style={{ marginBottom: 6 }}>Set the scale</div>
            <p className="muted" style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>How long is the line you just drew, in real units? Everything measured after uses this scale.</p>
            <div className="row" style={{ gap: 8 }}>
              <input className="input mono" autoFocus type="number" value={calibrating.value} onChange={(e) => setCalibrating((c) => c && { ...c, value: e.target.value })} onKeyDown={(e) => e.key === "Enter" && commitCalibration()} placeholder="e.g. 6.0" style={{ flex: 1 }} />
              <select className="select" style={{ width: 82 }} value={calibrating.unit} onChange={(e) => setCalibrating((c) => c && { ...c, unit: e.target.value })}>{["mm", "m", "ft", "in"].map((u) => <option key={u}>{u}</option>)}</select>
            </div>
            <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button className="btn ghost sm" onClick={() => setCalibrating(null)}>Cancel</button>
              <button className="btn accent sm" onClick={commitCalibration}><Icon.check width={13} />Set scale</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  return createPortal(overlay, document.body);
}

const navBtn: React.CSSProperties = { position: "absolute", top: "50%", transform: "translateY(-50%)", width: 42, height: 42, background: "var(--card)", border: "1px solid var(--line)", boxShadow: "var(--shadow-md)", zIndex: 11 };
function railStyle(on: boolean): React.CSSProperties {
  return { width: 34, height: 34, background: on ? "var(--accent)" : "transparent", color: on ? "#fff" : "var(--ink-2)", border: "none" };
}
